/* eslint-disable */
import { defineStore, storeToRefs } from 'pinia';
import { ref, inject, computed } from 'vue';
import { Cookies, LocalStorage } from 'quasar';
import { useBrandKeywordsStore } from 'stores/brandKeywords';

export const useAccountStore = defineStore('account', () => {
  const axios = inject('axios');
  const brandKeywordsStore = useBrandKeywordsStore();
  const { positiveKeywords } = storeToRefs(brandKeywordsStore);

  const account = ref(null);

  const keywordsLimit = computed(() => {
    const { canManageReplyKeywords } = account.value.limits;
    return canManageReplyKeywords?.amount?.limit;
  });

  const isUnlimitedKeywords = computed(() => {
    return keywordsLimit.value === -1;
  });

  function getTotalKeywordLimits(keywords = positiveKeywords.value) {
    if (!isUnlimitedKeywords.value) {
      const { canManageReplyKeywords } = account.value.limits;
      return [keywords.length, canManageReplyKeywords.amount?.limit];
    }

    return null;
  }
  function canAddKeywords(keywords) {
    const [used, limit] = getTotalKeywordLimits(keywords);
    return used < limit;
  }
  /* eslint-disable */
  const isMyAccount = computed(() => {
    if (account.value) {
      return [
          'funkiblog',
          'funkibooks',
          'funkireply',
          'funkisocial'
        ].every((service) => !account.value.availableServices[service])
        && account.value.availableServices.funkitools;
    }
  })

  const keywordsLimits = computed(() => account.value.limits.canGetKeywordsSuggestionsUnlimitedTime);
  const keywordsLimitsByTime = computed(() => account.value.limits.canGetKeywordsSuggestionsLimitedTime);
  const hasKeywordsLimits = computed(() => {
    if (keywordsLimits.value?.amount?.limit === -1 || keywordsLimitsByTime.value?.amount?.limit === -1) {
      return false;
    }

    const unlimited = keywordsLimits.value
    ? keywordsLimits.value.amount.limit - keywordsLimits.value.amount.uses <= 0
    : true;

    const byTime = keywordsLimitsByTime.value
    ? keywordsLimitsByTime.value.amount.limit - keywordsLimitsByTime.value.amount.uses <= 0
    : true;

    return unlimited && byTime;
  });

  const aiPostsLimits = computed(() => account.value.limits.canManageAIBlogPostsUnlimitedTime);
  const aiPostsByTimeLimits = computed(() => account.value.limits.canManageAIBlogPostsLimitedTime);
  const manualPostsLimits = computed(() => account.value.limits.canManageManualBlogPosts);
  const hasAIPostsLimits = computed(() => {
    if (aiPostsLimits.value?.amount?.limit === -1 || aiPostsByTimeLimits.value?.amount?.limit === -1) {
      return false;
    }

    const unlimited = aiPostsLimits.value
    ? aiPostsLimits.value.amount.limit - aiPostsLimits.value.amount.uses <= 0
    : true;

    const byTime = aiPostsByTimeLimits.value
    ? aiPostsByTimeLimits.value.amount.limit - aiPostsByTimeLimits.value.amount.uses <= 0
    : true;

    return unlimited && byTime;
  });
  const hasManualPostsLimits = computed(() => {
    if (manualPostsLimits.value?.amount?.limit === -1) {
      return false;
    }

    return manualPostsLimits.value
    ? manualPostsLimits.value.amount.limit - manualPostsLimits.value.amount.uses <= 0
    : true;
  });
  const availableServices = computed(() => {
    if (!account.value) {
      return [];
    }
    const services = Object.keys(account.value.availableServices);
    return services.filter(
      (item) => (item !== 'funkitools' && account.value.availableServices[item]),
    );
  });
  const calendarLocale = computed(() => account.value.currentLanguage.split('_').map((item) => item.toLowerCase()).join('-'));

  async function getAccount() {
    const { data } = (await axios('/user/identity')).data;
    account.value = data;
  }

  async function updateAccount(form) {
    const { data } = (await axios.put('/user/identity', form)).data;
    account.value = data;
  }

  async function auth(form) {
    const { data } = (await axios.post('/user/auth', {
      ...form,
      password: btoa(form.password),
    })).data;

    Cookies.set('accessToken', data.token, {
      expires: 7,
      path: '/',
    });
    await getAccount();
  }

  function logout() {
    account.value = null;
    Cookies.remove('accessToken', {
      path: '/',
    });
    LocalStorage.remove('blogGuid');
  }

  function setNewEmail(newEmail) {
    account.value.email.new = newEmail;
  }

  async function confirmEmail(confirmCode) {
    const { data } = (await axios.put('user/change/email/confirm', { confirmCode })).data;
    account.value = data;
  }

  async function updateSlug(slug) {
    const { data } = (await axios.put('user/change/slug', { slug })).data;
    account.value = data;
  }

  async function markPopupRead() {
    const { data } = (await axios.post('/user/mark-popup-as-readed/upgrade/blog')).data;
    return data;
  }

  return {
    account,
    getAccount,
    updateAccount,
    auth,
    logout,
    setNewEmail,
    confirmEmail,
    updateSlug,
    hasKeywordsLimits,
    hasAIPostsLimits,
    hasManualPostsLimits,
    markPopupRead,
    availableServices,
    calendarLocale,
    isUnlimitedKeywords,
    getTotalKeywordLimits,
    canAddKeywords,
    keywordsLimit,
    isMyAccount,
  };
});
